import { inject, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, type ActivatedRouteSnapshot } from '@angular/router';
import { filter, map } from 'rxjs';

/**
 * 현재 활성 라우트 트리(root→leaf)의 `data`를 얕게 병합해 반환하는 signal — leaf 쪽 값이
 * 이긴다. 앱 루트 컴포넌트가 `<sbe-app-shell>`을 감싸며 header/title/navItems 등을
 * 라우트별로 다르게 넣어야 할 때 쓴다.
 *
 * `libs/client/spinoff-platform/.../spinoff-active-org-context.service.ts`의 "라우트
 * 스냅샷 체인을 직접 순회" 방식을 일반화한 것이다 — `paramsInheritanceStrategy: 'always'`를
 * 전역 설정하지 않은 앱에서도 동작해야 하므로 Angular의 자동 data 상속에 기대지 않는다.
 *
 * 서비스 클래스가 아니라 `inject()` 기반 순수 함수로 둔다 — org 컨텍스트 서비스와 달리
 * sticky 상태나 인증 경계 판단 같은 도메인 로직이 없는 범용 라우팅 유틸리티라
 * `docs/conventions/conventions-angular-practices.md`(§라우트 파라미터와 이벤트)가
 * 서비스에 요구하는 "컴포넌트가 input()으로 받아 push" 원칙의 예외 사유에 해당하지
 * 않는다 — 컴포넌트 필드 초기화/생성자 같은 injection context에서 `toSignal`처럼
 * 직접 호출해 쓴다.
 *
 * @param defaults 라우트 어디에도 없는 키에 대한 기본값
 */
export function injectShellRouteData<T extends Record<string, unknown>>(defaults: T): Signal<T> {
  const router = inject(Router);

  const resolve = (): T => mergeRouteData(router.routerState.snapshot.root, defaults);

  return toSignal(
    router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(resolve),
    ),
    { initialValue: resolve() },
  );
}

// leaf 쪽 값이 우선하도록 root→leaf 순서로 병합한다. `firstChild`만 쓰면 named/auxiliary
// outlet이 함께 활성화된 라우트 구조에서 primary가 아닌 outlet의 자식을 따라갈 수 있어
// (Gemini 리뷰 지적), 각 단계에서 `outlet === 'primary'`인 자식을 먼저 찾고 없을 때만
// firstChild로 폴백한다.
function mergeRouteData<T extends Record<string, unknown>>(
  root: ActivatedRouteSnapshot,
  defaults: T,
): T {
  let merged: T = { ...defaults, ...(root.data as Partial<T>) };
  let snapshot: ActivatedRouteSnapshot | null = root;
  while (snapshot) {
    const nextSnapshot: ActivatedRouteSnapshot | null =
      snapshot.children.find((child) => child.outlet === 'primary') ?? snapshot.firstChild;
    if (!nextSnapshot) break;
    snapshot = nextSnapshot;
    merged = { ...merged, ...(snapshot.data as Partial<T>) };
  }
  return merged;
}
